/**
 * Sales reports.
 *
 * `net_sales_change` is `string | null`, and null means "there was no prior
 * period" — not that trade was flat. A UI that renders it as 0% tells a
 * restaurant in its first week that it is not growing.
 *
 * Exports go through `useDownload`, which fetches with the session's token
 * and saves the file under the name the server gave it. They used to be plain
 * links, which cannot carry a bearer token, so every download opened a page
 * reading "Sign in to continue".
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from './client';

import { useApi } from './provider';

export interface DayLine {
  business_date: string;
  orders: number;
  covers: number;
  net: string;
}

export interface SalesSummary {
  period: { from_date: string; to_date: string };
  currency: string;
  net_sales: string;
  orders: number;
  covers: number;
  average_spend: string;
  /** Null when there is no prior period. Not zero — that would be a claim. */
  net_sales_change: string | null;
  /** What the same length of time before took. */
  previous_net_sales: string;
  days: DayLine[];
}

export interface RankedLine {
  key: string;
  label: string;
  quantity: string;
  net: string;
  /** Share of the whole period's takings, not of the rows returned. */
  share: string;
}

export interface HourLine {
  hour: number;
  orders: number;
  net: string;
}

export interface LabourLine {
  business_date: string;
  net_sales: string;
  labour_cost: string;
  labour_percentage: string;
}

interface Range {
  locationId: string | undefined;
  fromDate: string;
  toDate: string;
}

function query({ locationId, fromDate, toDate }: Range): string {
  return `location_id=${locationId}&from_date=${fromDate}&to_date=${toDate}`;
}

export function useSalesSummary(range: Range) {
  const api = useApi();
  return useQuery({
    queryKey: ['reports', 'sales', range.locationId ?? null, range.fromDate, range.toDate],
    queryFn: ({ signal }) => api.get<SalesSummary>(`/v1/reports/sales?${query(range)}`, signal),
    enabled: Boolean(range.locationId),
    staleTime: 60_000,
  });
}

export interface FoodCostLine {
  item_id: string;
  name: string;
  quantity: string;
  revenue: string;
  /** What one costs to make, priced at what the store paid. */
  unit_cost: string;
  cost: string;
  margin: string;
  /** Null where a dish sold for nothing. A zero would read as free food. */
  food_cost_percent: string | null;
}

export interface FoodCostSummary {
  from_date: string;
  to_date: string;
  /** Everything sold, costed or not. */
  revenue: string;
  /** The part with a recipe behind it, and what the percentage is against. */
  costed_revenue: string;
  cost: string;
  margin: string;
  food_cost_percent: string | null;
  /** Dishes sold with no recipe: takings counted, cost counted nowhere. */
  uncosted: string[];
  lines: FoodCostLine[];
}

/**
 * What the food cost, against what it sold for.
 *
 * The headline percentage is cost over *costed* revenue rather than over
 * everything sold, because a kitchen with recipes for the food and none for
 * the wine would otherwise read as far cheaper than it is — and that mistake
 * flatters, which is the direction somebody acts on.
 */
export function useFoodCost(range: Range) {
  const api = useApi();
  return useQuery({
    queryKey: ['reports', 'cost', range.locationId ?? null, range.fromDate, range.toDate],
    queryFn: ({ signal }) =>
      api.get<FoodCostSummary>(`/v1/reports/cost?${query(range)}`, signal),
    enabled: Boolean(range.locationId),
    staleTime: 60_000,
  });
}

export function useTopItems(range: Range, limit = 20) {
  const api = useApi();
  return useQuery({
    queryKey: [
      'reports',
      'items',
      range.locationId ?? null,
      range.fromDate,
      range.toDate,
      limit,
    ],
    queryFn: ({ signal }) =>
      api.get<RankedLine[]>(`/v1/reports/items?${query(range)}&limit=${limit}`, signal),
    enabled: Boolean(range.locationId),
    staleTime: 60_000,
  });
}

export function useByHour(range: Range, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ['reports', 'hours', range.locationId ?? null, range.fromDate, range.toDate],
    queryFn: ({ signal }) => api.get<HourLine[]>(`/v1/reports/hours?${query(range)}`, signal),
    enabled: enabled && Boolean(range.locationId),
    staleTime: 60_000,
  });
}

export function useBySeller(range: Range) {
  const api = useApi();
  return useQuery({
    queryKey: ['reports', 'sellers', range.locationId ?? null, range.fromDate, range.toDate],
    queryFn: ({ signal }) =>
      api.get<RankedLine[]>(`/v1/reports/sellers?${query(range)}`, signal),
    enabled: Boolean(range.locationId),
    staleTime: 60_000,
  });
}

export function useLabour(range: Range, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: ['reports', 'labour', range.locationId ?? null, range.fromDate, range.toDate],
    queryFn: ({ signal }) =>
      api.get<LabourLine[]>(`/v1/reports/labour?${query(range)}`, signal),
    enabled: enabled && Boolean(range.locationId),
    staleTime: 60_000,
  });
}

/** The path of a CSV export, for `useDownload`. */
export function csvUrl(report: 'sales' | 'items' | 'labour', range: Range): string {
  return `/v1/reports/${report}.csv?${query(range)}`;
}

/**
 * The path of the formatted spreadsheet: every report for the period in one
 * workbook. 402 below Professional.
 */
export function workbookUrl(range: Range): string {
  return `/v1/reports/workbook.xlsx?${query(range)}`;
}

/**
 * Fetch an export with the session's token and hand it to the browser to save.
 *
 * `fallbackName` is used only if the server does not name the file. A `body`
 * makes it a POST, for an export described by more than a query string.
 */
export function useDownload() {
  const api = useApi();
  return useMutation<void, ApiError, { path: string; fallbackName: string; body?: unknown }>({
    mutationFn: async ({ path, fallbackName, body }) => {
      const { blob, filename } = await api.download(path, body);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename ?? fallbackName;
      document.body.append(link);
      link.click();
      link.remove();
      // Revoked on the next tick: revoking synchronously can cancel the save
      // before the browser has started it.
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
    },
  });
}

/** Monday of the current week through today — what "this week" means midweek. */
export function thisWeek(today = new Date()): { fromDate: string; toDate: string } {
  const monday = new Date(today);
  monday.setDate(today.getDate() - ((today.getDay() + 6) % 7));
  return { fromDate: isoDate(monday), toDate: isoDate(today) };
}

export function lastNDays(
  days: number,
  today = new Date(),
): { fromDate: string; toDate: string } {
  const start = new Date(today);
  start.setDate(today.getDate() - (days - 1));
  return { fromDate: isoDate(start), toDate: isoDate(today) };
}

export function isoDate(value: Date): string {
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${value.getFullYear()}-${month}-${day}`;
}

/** "+12.5%" / "−8.0%" / "—". Null is an absence, and reads as one. */
/**
 * How sales moved against the same length of time before, in words an owner
 * reads without a calculator.
 *
 * A percentage is only honest between two real figures of a similar size.
 * "+2094.1%" over a week that took $25, or "−100.0%" on a Sunday morning
 * before the first coffee, is true and tells an owner nothing but alarm. So:
 * nothing before says so; nothing yet says so and what the time before took;
 * a change of more than double either way says the figure it came from; and
 * only an ordinary change is a percentage.
 */
export function describeChange(
  current: string,
  previous: string,
  percent: string | null,
  /** "week" or "30 days", as in "the week before". */
  span: string,
  money: (amount: string) => string,
): string {
  if (percent === null) return `nothing the ${span} before to compare`;
  const now = Number(current);
  const before = Number(previous);
  if (!Number.isFinite(now) || !Number.isFinite(before)) return '—';
  if (before === 0) {
    return now === 0
      ? `nothing yet, and nothing the ${span} before`
      : `nothing the ${span} before`;
  }
  if (now === 0) return `nothing yet · ${money(previous)} the ${span} before`;
  const value = Number(percent);
  if (Math.abs(value) > 100) {
    return `${now > before ? 'up' : 'down'} from ${money(previous)} the ${span} before`;
  }
  return `${formatChange(percent)} on the ${span} before`;
}

export function formatChange(percent: string | null): string {
  if (percent === null) return '—';
  const value = Number(percent);
  if (!Number.isFinite(value)) return '—';
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  return `${sign}${Math.abs(value).toFixed(1)}%`;
}

// --------------------------------------------------------------------------- //
// Scheduled reports
// --------------------------------------------------------------------------- //

export type ReportFrequency = 'daily' | 'weekly' | 'monthly';

/** A period's reports, emailed as a spreadsheet on a cadence. */
export interface ReportSchedule {
  id: string;
  location_id: string;
  frequency: ReportFrequency;
  /** On the restaurant's clock, 0–23. */
  hour: number;
  /** Monday is 0. Weekly only. */
  weekday: number | null;
  /** "Every Monday at 07:00, for the week before". */
  description: string;
  recipients: string[];
  include_labour: boolean;
  is_active: boolean;
  next_run_at: string;
  last_sent_at: string | null;
  /** Why the last run did not go, in words. Cleared by the next that does. */
  last_error: string | null;
}

export interface ReportScheduleInput {
  locationId: string;
  frequency: ReportFrequency;
  hour: number;
  weekday: number | null;
  recipients: string[];
  includeLabour: boolean;
}

/** Every report a site sends on a clock. Readable on any plan. */
export function useReportSchedules(locationId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['reports', 'schedules', locationId ?? null],
    queryFn: ({ signal }) =>
      api.get<ReportSchedule[]>(`/v1/reports/schedules?location_id=${locationId}`, signal),
    enabled: Boolean(locationId),
  });
}

function useScheduleMutation<TInput>(
  send: (api: ReturnType<typeof useApi>, input: TInput) => Promise<unknown>,
) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<unknown, ApiError, TInput>({
    mutationFn: (input) => send(api, input),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['reports', 'schedules'] });
    },
  });
}

/** 402 below Professional; 409 with `field` for a cadence that makes no sense. */
export function useCreateReportSchedule() {
  return useScheduleMutation<ReportScheduleInput>((api, input) =>
    api.post<ReportSchedule>('/v1/reports/schedules', {
      location_id: input.locationId,
      frequency: input.frequency,
      hour: input.hour,
      weekday: input.frequency === 'weekly' ? input.weekday : null,
      recipients: input.recipients,
      include_labour: input.includeLabour,
    }),
  );
}

/** Pause or resume. Resuming starts from now; nothing is owed for the gap. */
export function usePauseReportSchedule() {
  return useScheduleMutation<{ scheduleId: string; active: boolean }>((api, input) =>
    api.request<ReportSchedule>(`/v1/reports/schedules/${input.scheduleId}`, {
      method: 'PATCH',
      body: { is_active: input.active },
    }),
  );
}

/** Stop it for good. Allowed on any plan: stopping mail never needs an upgrade. */
export function useRemoveReportSchedule() {
  return useScheduleMutation<{ scheduleId: string }>((api, input) =>
    api.request<void>(`/v1/reports/schedules/${input.scheduleId}`, { method: 'DELETE' }),
  );
}

/** Send what the next run would, now. 502 when the mail server refuses. */
export function useSendReportNow() {
  return useScheduleMutation<{ scheduleId: string }>((api, input) =>
    api.post<ReportSchedule>(`/v1/reports/schedules/${input.scheduleId}/send-now`),
  );
}
